import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { isAxiosCanceled } from '@/interceptors/axiosInstance.ts';
import { isAxiosError } from 'axios';
import { devtools } from 'zustand/middleware';
import type { StateCreator } from 'zustand';
import type { LookupOption } from '@/features/lookup/lookup-types.ts';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ── Shared retry logic ────────────────────────────────────────────────────────

export const retryPolicy = (failureCount: number, error: unknown) => {
  if (isAxiosCanceled(error)) return false;
  if (isAxiosError(error) && error.response) return false;
  return failureCount < 3;
};

export const isDev = import.meta.env.MODE !== 'production' || import.meta.env.DEV;

const enableDevtools = isDev && import.meta.env.VITE_ENABLE_DEVTOOLS === 'true';

type DevtoolsArgs = Parameters<typeof devtools>;

export const withDevtools = enableDevtools
  ? devtools
  : ((<T>(initializer: StateCreator<T, [], []>, _options?: DevtoolsArgs[1]) =>
      initializer) as typeof devtools);

export function capitalizeFirstLetter(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function formatDate(value: string | Date | null | undefined, withTime = false): string {
  if (!value) return '–';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return typeof value === 'string' ? value : '–';

  const dateOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };

  if (withTime) {
    return d.toLocaleString('de-DE', {
      ...dateOptions,
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return d.toLocaleDateString('de-DE', dateOptions);
}

export function formatDateWithTime(date: Date | undefined): string {
  if (date == undefined) return '-';
  const d = new Date(date);

  if (isNaN(d.getTime())) {
    throw new Error('Ungültiges Datum');
  }

  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();

  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const seconds = d.getSeconds().toString().padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
}

export function normalizeString(s: string) {
  return s
    .toLowerCase()
    .replace('ä', 'ae')
    .replace('ö', 'oe')
    .replace('ü', 'ue')
    .replace('ß', 'ss')
    .replace(/\s+/g, '');
}

export type SelectOption<V = string | number> = {
  label: string;
  value: V;
  description?: string;
};

export function toOptions<T, V = string | number>(
  items: T[],
  getLabel: (item: T) => string,
  getValue: (item: T) => V,
  getDescription?: (item: T) => string | undefined
): SelectOption<V>[] {
  return items.map((item) => ({
    label: getLabel(item),
    value: getValue(item),
    ...(getDescription ? { description: getDescription(item) } : {}),
  }));
}

export function lookupToOptions<T extends LookupOption>(items: T[]): SelectOption<string>[] {
  return items.map((item) => ({ label: item.label, value: item.code }));
}
